import { Grid, Typography } from "@mui/material";
import {
  ChartsGrid,
  ChartsLegend,
  ChartsXAxis,
  ChartsYAxis,
  lineElementClasses,
  LinePlot,
  markElementClasses,
  MarkPlot,
} from "@mui/x-charts";
import { LineChartPro } from "@mui/x-charts-pro";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { format } from "date-fns";
import { CustomItemTooltip } from "../../../../../ResuableChartTooltip/CustomItemTooltip";
import { useSelector } from "react-redux";

const PerformanceDashboardRealTimePerformance = () => {
  const { exequteQueryContentQuery5, exequteQueryContentQuery6, selectedPage } =
    useSelector((state: any) => state.pageSpeedPerformance);
  const GraphLCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Time_Performance_LCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificLCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Time_Performance_LCP || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphSpeedIndex =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat(
        (item?.Avg_Real_Time_Performance_Speed_Index || 0).toFixed(2)
      ),
    })) || [];

  const GraphPageSpecificSpeedIndex =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat(
          (item?.Avg_Real_Time_Performance_Speed_Index || 0).toFixed(2)
        ),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphCLS =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Time_Performance_CLS || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificCLS =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Time_Performance_CLS || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphFCP =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Time_Performance_FCP || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificFCP =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Time_Performance_FCP || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  const GraphTBT =
    exequteQueryContentQuery5?.payload?.map((item: any) => ({
      x: format(new Date(item?.DATE), "dd-MM-yyyy") || "No Date",
      y: parseFloat((item?.Avg_Real_Time_Performance_TBT || 0).toFixed(2)),
    })) || [];

  const GraphPageSpecificTBT =
    exequteQueryContentQuery6?.payload
      ?.filter((item: any) => item.page_name === selectedPage?.page_name)
      ?.map((item: any) => ({
        x: format(new Date(item?.DATE), "dd-MM-yyyy"), // Format the date
        y: parseFloat((item?.Real_Time_Performance_TBT || 0).toFixed(2)),
      })) || []; // Fallback to an empty array if data is unavailable

  return (
    <>
      <Grid container spacing={3} mb={2} className="ratedTableWrap">
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            LCP (Largest Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={selectedPage ? GraphPageSpecificLCP : GraphLCP}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                showMark: true,
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <ChartsReferenceLine
              y={4}
              lineStyle={{ stroke: "#5de2e2", strokeDasharray: "3" }}
            />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            TBT (Total Blocking Time )
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={selectedPage ? GraphPageSpecificTBT : GraphTBT}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                showMark: true,
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            CLS (Cumulative Layout Shift)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Milliseconds
          </Typography>
          <LineChartPro
            dataset={selectedPage ? GraphPageSpecificCLS : GraphCLS}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                showMark: true,
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal={false} />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            FCP (First Contentful Paint)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={selectedPage ? GraphPageSpecificFCP : GraphFCP}
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                showMark: true,
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
        </Grid>
        <Grid item xs={6}>
          <Typography sx={{ fontSize: "18px", color: "#000000" }}>
            Speed Index (Daily Average)
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#00000099" }}>
            in Seconds
          </Typography>
          <LineChartPro
            dataset={
              selectedPage ? GraphPageSpecificSpeedIndex : GraphSpeedIndex
            }
            series={[
              {
                type: "line",
                dataKey: "y",
                color: "#6200EE",
                showMark: true,
                curve: "linear",
              },
            ]}
            xAxis={[{ scaleType: "point", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                disableLine: true,
                disableTicks: true,
                min: 0,
                position: "right",
              },
            ]}
            height={350}
            margin={{ left: 32, right: 42, top: 25, bottom: 30 }}
            sx={{
              [`& .${lineElementClasses.root}`]: {
                strokeWidth: 2,
              },
              [`& .${markElementClasses.root}`]: {
                scale: "0.6",
                strokeWidth: 2,
                "&:hover": {
                  scale: "1.2",
                  strokeWidth: 5,
                  opacity: "0.6",
                },
              },
            }}
            leftAxis={null}
            rightAxis={{}}
          >
            <ChartsGrid horizontal />
            <CustomItemTooltip />
            <ChartsXAxis />
            <ChartsYAxis />
            <LinePlot />
            <MarkPlot />
            <ChartsLegend direction="row" />
          </LineChartPro>
        </Grid>
      </Grid>
    </>
  );
};

export default PerformanceDashboardRealTimePerformance;
